import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

function collapseExpand(transitionIn = '250ms cubic-bezier(0.445, 0.05, 0.55, 0.95)', transitionOut = '250ms ease-in-out') {
    return trigger('collapseExpand', [
        transition(':enter', [
            style({ height: 0, opacity: 0, overflow: 'hidden' }),
            animate(transitionIn, style({ height: '*', opacity: 1 })),
        ]),
        transition(':leave', [
            style({ height: '*', opacity: 1, overflow: 'hidden' }),
            animate(transitionOut, style({ height: 0, opacity: 0 })),
        ]),
    ]);
}

function debounceDecorator(delay = 300) {
    return function (target, propertyKey, descriptor) {
        const timeoutKey = Symbol();
        const original = descriptor.value;
        descriptor.value = function (...args) {
            clearTimeout(this[timeoutKey]);
            this[timeoutKey] = setTimeout(() => original.apply(this, args), delay);
        };
        return descriptor;
    };
}

var EquisColors;
(function (EquisColors) {
    // Orange
    // Orange. Use for brand identification
    EquisColors["Orange_200"] = "#fee4d9";
    EquisColors["Orange_300"] = "#fec1a6";
    EquisColors["Orange_400"] = "#fe9e73";
    EquisColors["Orange_500"] = "#fe5000";
    EquisColors["Orange_600"] = "#cb4000";
    EquisColors["Orange_700"] = "#983000";
    EquisColors["Orange_800"] = "#7e2700";
    // Grayscale
    // Use for user interface variations
    EquisColors["Grayscale_100"] = "#ffffff";
    EquisColors["Grayscale_200"] = "#fafafa";
    EquisColors["Grayscale_300"] = "#e8e8e8";
    EquisColors["Grayscale_400"] = "#d1d1d1";
    EquisColors["Grayscale_500"] = "#b2b2b2";
    EquisColors["Grayscale_600"] = "#767676";
    EquisColors["Grayscale_700"] = "#5b5b5b";
    EquisColors["Grayscale_800"] = "#333333";
    EquisColors["Grayscale_900"] = "#000000";
    // Yellow
    // Yellow. Use for warning feedback
    EquisColors["Yellow_200"] = "#fff3db";
    EquisColors["Yellow_300"] = "#ffe4ab";
    EquisColors["Yellow_400"] = "#ffd987";
    EquisColors["Yellow_500"] = "#ffb30f";
    EquisColors["Yellow_600"] = "#996b09";
    EquisColors["Yellow_700"] = "#735006";
    EquisColors["Yellow_800"] = "#5a3f05";
    // Red
    // Red. Use for error and danger feedback
    EquisColors["Red_200"] = "#fbe9e9";
    EquisColors["Red_300"] = "#f4bbbb";
    EquisColors["Red_400"] = "#ed8f8f";
    EquisColors["Red_500"] = "#db1f1f";
    EquisColors["Red_600"] = "#ba1a1a";
    EquisColors["Red_700"] = "#991515";
    EquisColors["Red_800"] = "#791111";
    // Green
    // Green. Use for success and completion feedback// Info
    EquisColors["Green_200"] = "#e4f3e7";
    EquisColors["Green_300"] = "#c2e5c9";
    EquisColors["Green_400"] = "#86cc95";
    EquisColors["Green_500"] = "#24a33f";
    EquisColors["Green_600"] = "#19722c";
    EquisColors["Green_700"] = "#11511f";
    EquisColors["Green_800"] = "#0e411a";
    // Blue
    // Blue. Use for interface information and non critical feedback
    EquisColors["Blue_200"] = "#e6eefa";
    EquisColors["Blue_300"] = "#c3d7f3";
    EquisColors["Blue_400"] = "#7ba7e5";
    EquisColors["Blue_500"] = "#1060d0";
    EquisColors["Blue_600"] = "#0d4ca6";
    EquisColors["Blue_700"] = "#0a397c";
    EquisColors["Blue_800"] = "#083068";
    // Violet
    // Violet. Use for brand identification (Naranja X)
    EquisColors["Violet_200"] = "#f3eef6";
    EquisColors["Violet_300"] = "#b999cb";
    EquisColors["Violet_400"] = "#844da5";
    EquisColors["Violet_500"] = "#50007f";
    EquisColors["Violet_600"] = "#44006c";
    EquisColors["Violet_700"] = "#3a005c";
    EquisColors["Violet_800"] = "#30004c";
    // Teal
    EquisColors["Teal_200"] = "#e7f5f7";
    EquisColors["Teal_300"] = "#a9dee3";
    EquisColors["Teal_400"] = "#78cbd3";
    EquisColors["Teal_500"] = "#0ba1b1";
    EquisColors["Teal_600"] = "#07717c";
    EquisColors["Teal_700"] = "#055058";
    EquisColors["Teal_800"] = "#044046";
})(EquisColors || (EquisColors = {}));

var EquisComponentScope;
(function (EquisComponentScope) {
    EquisComponentScope["WHITE"] = "white";
    EquisComponentScope["GRAY"] = "gray";
    EquisComponentScope["TRANSPARENT"] = "transparent";
})(EquisComponentScope || (EquisComponentScope = {}));

var EquisComponentStatus;
(function (EquisComponentStatus) {
    EquisComponentStatus["ACTIVE"] = "active";
    EquisComponentStatus["ERROR"] = "error";
    EquisComponentStatus["DEFAULT"] = "default";
})(EquisComponentStatus || (EquisComponentStatus = {}));

var EquisComponentTheme;
(function (EquisComponentTheme) {
    EquisComponentTheme["DARK"] = "dark";
    EquisComponentTheme["LIGHT"] = "light";
})(EquisComponentTheme || (EquisComponentTheme = {}));

var EquisComponentType;
(function (EquisComponentType) {
    EquisComponentType["PRIMARY"] = "primary";
    EquisComponentType["SECONDARY"] = "secondary";
    EquisComponentType["STICKY"] = "sticky";
    EquisComponentType["LINK"] = "link";
    EquisComponentType["HEADER"] = "header";
    EquisComponentType["RADIO"] = "radio";
    EquisComponentType["INPUT"] = "input";
    EquisComponentType["SELECT"] = "select";
    EquisComponentType["CHECK"] = "check";
    EquisComponentType["NEGATIVE"] = "negative";
})(EquisComponentType || (EquisComponentType = {}));

var EquisIconsType;
(function (EquisIconsType) {
    EquisIconsType["CHECK_CIRCLE_FILL"] = "icon-check-circle-fill";
    EquisIconsType["ALERT_TRIANGLE_FILL"] = "icon-alert-triangle-fill";
    EquisIconsType["ALERT_CIRCLE_FILL"] = "icon-alert-circle-fill";
    EquisIconsType["ALERT_DIAMOND_FILL"] = "icon-alert-diamond-fill";
    EquisIconsType["INFO_ALT_CIRCLE_FILL"] = "icon-info-alt-circle-fill";
    EquisIconsType["CROSS"] = "icon-cross";
    EquisIconsType["ANGLE_DOWN"] = "icon-angle-down";
    EquisIconsType["BULLET"] = "icon-bullet";
    EquisIconsType["CHIP_CHECK"] = "icon-chip-check";
    EquisIconsType["CHIP_CROSS"] = "icon-chip-cross";
    EquisIconsType["CLOCK"] = "icon-clock-fill";
    EquisIconsType["INFO_CIRCLE_FILL"] = "icon-info-circle-fill";
    EquisIconsType["DEFAULT"] = "";
})(EquisIconsType || (EquisIconsType = {}));

const EquisKeyPressType = {
    ENTER: 'Enter',
    ESCAPE: 'Escape',
    TAB: 'Tab',
    SPACE: ' ',
};

var EquisPositionsType;
(function (EquisPositionsType) {
    EquisPositionsType["BOTTOM"] = "bottom";
    EquisPositionsType["TOP"] = "top";
    EquisPositionsType["RIGHT"] = "right";
    EquisPositionsType["LEFT"] = "left";
    EquisPositionsType["BOTTOM_RIGHT"] = "bottom-right";
    EquisPositionsType["BOTTOM_LEFT"] = "bottom-left";
    EquisPositionsType["TOP_RIGHT"] = "top-right";
    EquisPositionsType["TOP_LEFT"] = "top-left";
    EquisPositionsType["LEFT_TOP"] = "left-top";
    EquisPositionsType["LEFT_BOTTOM"] = "left-bottom";
    EquisPositionsType["RIGHT_TOP"] = "right-top";
    EquisPositionsType["RIGHT_BOTTOM"] = "right-bottom";
    EquisPositionsType["TOP_LEFT_ICON"] = "top-left-icon";
})(EquisPositionsType || (EquisPositionsType = {}));

var EquisSizes;
(function (EquisSizes) {
    EquisSizes["EXTRA_SMALL"] = "extra-small";
    EquisSizes["SMALL"] = "small";
    EquisSizes["MEDIUM"] = "medium";
    EquisSizes["LARGE"] = "large";
    EquisSizes["EXTRA_LARGE"] = "extra-large";
    EquisSizes["EXTRA_EXTRA_LARGE"] = "extra-extra-large";
})(EquisSizes || (EquisSizes = {}));

var StyleClassName;
(function (StyleClassName) {
    StyleClassName["AVATAR"] = "avatar";
    StyleClassName["ICON"] = "icon";
    StyleClassName["ACTION"] = "action";
    StyleClassName["DISABLED"] = "disabled";
})(StyleClassName || (StyleClassName = {}));

var StyleType;
(function (StyleType) {
    StyleType["WARNING"] = "warning";
    StyleType["INFO"] = "info";
    StyleType["SUCCESS"] = "success";
    StyleType["ERROR"] = "error";
    StyleType["NEUTRAL"] = "neutral";
    StyleType["BRAND"] = "brand";
    StyleType["DISABLED"] = "disabled";
    StyleType["PROCESS"] = "process";
})(StyleType || (StyleType = {}));

var StyleVars;
(function (StyleVars) {
    StyleVars["VISIBLE"] = "visible";
    StyleVars["HIDDEN"] = "hidden";
    StyleVars["NONE"] = "none";
})(StyleVars || (StyleVars = {}));
var StyleVarDirection;
(function (StyleVarDirection) {
    StyleVarDirection["UP"] = "Up";
    StyleVarDirection["DOWN"] = "Down";
})(StyleVarDirection || (StyleVarDirection = {}));
var StyleVarTransform;
(function (StyleVarTransform) {
    StyleVarTransform["UP"] = "Up";
    StyleVarTransform["DOWN"] = "Down";
})(StyleVarTransform || (StyleVarTransform = {}));
var StyleVarsDisplay;
(function (StyleVarsDisplay) {
    StyleVarsDisplay["NONE"] = "none";
})(StyleVarsDisplay || (StyleVarsDisplay = {}));
var StyleVarsFont;
(function (StyleVarsFont) {
    StyleVarsFont["BOLD"] = "bold";
    StyleVarsFont["ITALIC"] = "italic";
})(StyleVarsFont || (StyleVarsFont = {}));

var LinkTargetType;
(function (LinkTargetType) {
    LinkTargetType["SELF"] = "_self";
    LinkTargetType["BLANK"] = "_blank";
})(LinkTargetType || (LinkTargetType = {}));

var EquisCurrencyTypes;
(function (EquisCurrencyTypes) {
    EquisCurrencyTypes["USD"] = "U$D ";
    EquisCurrencyTypes["ARS"] = "$";
})(EquisCurrencyTypes || (EquisCurrencyTypes = {}));

var EquisAvatarColors;
(function (EquisAvatarColors) {
    EquisAvatarColors["Violet"] = "#BF9DF2";
    EquisAvatarColors["Light_blue"] = "#47ABFF";
    EquisAvatarColors["Pink"] = "#FD6077";
    EquisAvatarColors["Orange"] = "#F68E1A";
})(EquisAvatarColors || (EquisAvatarColors = {}));

class EquisUtilsModule {
    static forRoot() {
        return {
            ngModule: EquisUtilsModule,
        };
    }
}
EquisUtilsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisUtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EquisUtilsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: EquisUtilsModule, imports: [CommonModule, ReactiveFormsModule], exports: [ReactiveFormsModule] });
EquisUtilsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisUtilsModule, imports: [CommonModule, ReactiveFormsModule, ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquisUtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ReactiveFormsModule],
                    exports: [ReactiveFormsModule],
                }]
        }] });

/**
 * @file Automatically generated by barrelsby.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { EquisAvatarColors, EquisColors, EquisComponentScope, EquisComponentStatus, EquisComponentTheme, EquisComponentType, EquisCurrencyTypes, EquisIconsType, EquisKeyPressType, EquisPositionsType, EquisSizes, EquisUtilsModule, LinkTargetType, StyleClassName, StyleType, StyleVarDirection, StyleVarTransform, StyleVars, StyleVarsDisplay, StyleVarsFont, collapseExpand, debounceDecorator };
